import { useState, useEffect } from "react";
import { Loader2, Check, AlertCircle, GraduationCap } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

interface RoomExamGradingProps {
  isOpen: boolean;
  attemptId: string | null;
  onComplete: (result: { score: number; attemptId: string; totalCorrect: number; totalQuestions: number }) => void;
  onError: (error: string) => void;
}

const gradingSteps = [
  "Submitting your answers",
  "Analyzing your responses",
  "Grading each question",
  "Calculating your score",
  "Preparing your results",
];

export const RoomExamGrading = ({ isOpen, attemptId, onComplete, onError }: RoomExamGradingProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [hasError, setHasError] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setCompletedSteps([]);
      setHasError(false);
    }
  }, [isOpen]);

  // Animate steps progressively
  useEffect(() => {
    if (!isOpen || hasError) return;

    const timeouts: NodeJS.Timeout[] = [];

    gradingSteps.forEach((_, index) => {
      const timeout = setTimeout(
        () => {
          if (index < gradingSteps.length - 1) {
            setCompletedSteps((prev) => [...prev, index]);
            setCurrentStep(index + 1);
          }
        },
        (index + 1) * 1800,
      );

      timeouts.push(timeout);
    });

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, [isOpen, hasError]);

  // Handle grading completion externally
  const completeAllSteps = () => {
    setCompletedSteps([0, 1, 2, 3, 4]);
  };

  // Handle error state
  const showError = () => {
    setHasError(true);
  };

  // Expose methods via parent coordination through props callback pattern
  useEffect(() => {
    if (isOpen && attemptId) {
      // This effect is for visual feedback - actual grading is handled by parent
    }
  }, [isOpen, attemptId]);

  const getStepState = (stepIndex: number) => {
    if (hasError && stepIndex === currentStep) {
      return "error";
    }
    if (completedSteps.includes(stepIndex)) {
      return "completed";
    } else if (stepIndex === currentStep) {
      return "loading";
    }
    return "pending";
  };

  const getStepIcon = (stepIndex: number) => {
    const state = getStepState(stepIndex);
    const iconClasses = "h-4 w-4 sm:h-5 sm:w-5 mr-2 sm:mr-3 flex-shrink-0";

    switch (state) {
      case "loading":
        return <Loader2 className={`${iconClasses} animate-spin text-foreground`} />;
      case "completed":
        return <Check className={`${iconClasses} text-primary`} />;
      case "error":
        return <AlertCircle className={`${iconClasses} text-destructive`} />;
      default:
        return <div className={iconClasses} />;
    }
  };

  const getStepTextColor = (stepIndex: number) => {
    const state = getStepState(stepIndex);
    if (state === "error") return "text-destructive";
    return state === "pending" ? "text-muted-foreground" : "text-foreground";
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="w-[calc(100%-2rem)] max-w-[90vw] sm:max-w-md md:max-w-lg lg:max-w-xl border-border [&>button]:hidden mx-auto p-4 sm:p-6 md:p-8 lg:p-10"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Grading your exam</DialogTitle>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col items-center justify-center gap-5 sm:gap-6 md:gap-8 py-2 sm:py-4 md:py-6"
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <GraduationCap className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-primary flex-shrink-0" />
            <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-center bg-gradient-to-r from-foreground via-muted-foreground to-foreground bg-[length:200%_100%] bg-clip-text text-transparent animate-shimmer">
              Grading in progress...
            </h2>
          </div>
          <div className="space-y-3 sm:space-y-4 md:space-y-5 flex flex-col items-center w-full px-2 sm:px-4 md:px-6">
            {gradingSteps.map((step, index) => {
              const state = getStepState(index);
              const isVisible = state === "loading" || state === "completed" || state === "error";

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{
                    opacity: isVisible ? 1 : 0.4,
                    x: isVisible ? 0 : -10,
                  }}
                  transition={{ duration: 0.3, delay: isVisible ? 0.1 : 0 }}
                  className="flex items-center justify-center"
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={state}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.8, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      {getStepIcon(index)}
                    </motion.div>
                  </AnimatePresence>
                  <span className={`text-sm sm:text-base md:text-lg ${getStepTextColor(index)}`}>{step}</span>
                </motion.div>
              );
            })}
          </div>

          {hasError && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-xs sm:text-sm text-muted-foreground text-center"
            >
              Something went wrong. Please try again.
            </motion.p>
          )}
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};
